import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const MAIN_SITE = import.meta.env.VITE_MAIN_SITE_URL || 'https://nera.systems';

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isAssessment = location.pathname === '/' || location.pathname.startsWith('/assessment') || location.pathname.startsWith('/results');

  return (
    <header
      className="fixed top-0 left-0 right-0 z-[1000] bg-white transition-shadow duration-300"
      style={{
        boxShadow: scrolled
          ? '0 4px 12px rgba(0, 0, 0, 0.1)'
          : '0 2px 4px rgba(0, 0, 0, 0.05)',
      }}
    >
      <div className="w-full max-w-[1200px] mx-auto px-6">
        <nav className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <a href={MAIN_SITE} className="flex items-center gap-2">
            <img src="/images/logo-icon.svg" alt="Nera Icon" className="w-10 h-10" />
            <img src="/images/logo-text.svg" alt="Nera" className="h-5 w-auto" />
          </a>

          {/* Desktop Nav */}
          <ul className="hidden md:flex items-center gap-8">
            <li className="relative group">
              <button type="button" className="inline-flex items-center gap-1 text-sm font-medium text-[#333] group-hover:text-[#0B3D91] relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-[#00A3E0] after:transition-all group-hover:after:w-full">
                Solutions
                <span className="text-[10px] leading-none transition-transform duration-200 group-hover:rotate-180">▾</span>
              </button>
              <ul className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 absolute top-full left-1/2 -translate-x-1/2 mt-3 min-w-[210px] bg-white rounded-xl py-2 shadow-[0_12px_32px_rgba(0,74,169,0.14)] before:content-[''] before:absolute before:-top-3 before:left-0 before:right-0 before:h-3">
                <li><a href={`${MAIN_SITE}/solutions/financial-services`} className="block mx-1 px-4 py-2 rounded-lg text-sm font-medium text-[#333] whitespace-nowrap hover:text-[#0B3D91] hover:bg-[rgba(0,74,169,0.06)]">Financial Services &amp; Accounting</a></li>
                <li><a href={`${MAIN_SITE}/solutions/underwriters`} className="block mx-1 px-4 py-2 rounded-lg text-sm font-medium text-[#333] whitespace-nowrap hover:text-[#0B3D91] hover:bg-[rgba(0,74,169,0.06)]">Insurance Underwriters</a></li>
                <li><a href={`${MAIN_SITE}/solutions/cpg`} className="block mx-1 px-4 py-2 rounded-lg text-sm font-medium text-[#333] whitespace-nowrap hover:text-[#0B3D91] hover:bg-[rgba(0,74,169,0.06)]">CPG &amp; Brand Management</a></li>
                <li><a href={`${MAIN_SITE}/solutions/`} className="block mx-1 px-4 py-2 rounded-lg text-sm font-medium text-[#333] whitespace-nowrap hover:text-[#0B3D91] hover:bg-[rgba(0,74,169,0.06)]">All solutions →</a></li>
              </ul>
            </li>
            <li><a href={`${MAIN_SITE}/customers/`} className="text-sm font-medium text-[#333] hover:text-[#0B3D91] relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-[#00A3E0] after:transition-all hover:after:w-full">Customers</a></li>
            <li><a href={`${MAIN_SITE}/pricing`} className="text-sm font-medium text-[#333] hover:text-[#0B3D91] relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-[#00A3E0] after:transition-all hover:after:w-full">Pricing</a></li>
            <li><a href={`${MAIN_SITE}/trust`} className="text-sm font-medium text-[#333] hover:text-[#0B3D91] relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-[#00A3E0] after:transition-all hover:after:w-full">Trust Center</a></li>
            <li className="relative group">
              <button type="button" className="inline-flex items-center gap-1 text-sm font-medium text-[#333] group-hover:text-[#0B3D91] relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-[#00A3E0] after:transition-all group-hover:after:w-full">
                News
                <span className="text-[10px] leading-none transition-transform duration-200 group-hover:rotate-180">▾</span>
              </button>
              <ul className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 absolute top-full left-1/2 -translate-x-1/2 mt-3 min-w-[210px] bg-white rounded-xl py-2 shadow-[0_12px_32px_rgba(0,74,169,0.14)] before:content-[''] before:absolute before:-top-3 before:left-0 before:right-0 before:h-3">
                <li><a href={`${MAIN_SITE}/blogs/`} className="block mx-1 px-4 py-2 rounded-lg text-sm font-medium text-[#333] whitespace-nowrap hover:text-[#0B3D91] hover:bg-[rgba(0,74,169,0.06)]">Blogs</a></li>
                <li><a href={`${MAIN_SITE}/press/`} className="block mx-1 px-4 py-2 rounded-lg text-sm font-medium text-[#333] whitespace-nowrap hover:text-[#0B3D91] hover:bg-[rgba(0,74,169,0.06)]">Press</a></li>
                <li><a href={`${MAIN_SITE}/releases/`} className="block mx-1 px-4 py-2 rounded-lg text-sm font-medium text-[#333] whitespace-nowrap hover:text-[#0B3D91] hover:bg-[rgba(0,74,169,0.06)]">Release Notes</a></li>
              </ul>
            </li>
            <li><a href={`${MAIN_SITE}/contact`} className="text-sm font-medium text-[#333] hover:text-[#0B3D91] relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-[#00A3E0] after:transition-all hover:after:w-full">Contact</a></li>
            <li>
              <Link
                to="/"
                className={`text-sm font-medium relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:h-[2px] after:bg-[#00A3E0] after:transition-all ${
                  isAssessment
                    ? 'text-[#0B3D91] after:w-full'
                    : 'text-[#333] hover:text-[#0B3D91] after:w-0 hover:after:w-full'
                }`}
              >
                Assessment
              </Link>
            </li>
            <li>
              <a href="https://chatapp.nera.systems" className="text-sm font-semibold text-white bg-[#0B3D91] hover:bg-[#00A3E0] rounded-full px-5 py-2 whitespace-nowrap transition-colors">Try it now</a>
            </li>
          </ul>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden flex flex-col justify-between w-6 h-[18px] bg-transparent border-none cursor-pointer"
            aria-label="Toggle menu"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <span
              className={`block w-full h-[2px] bg-[#0B3D91] transition-all duration-300 ${
                menuOpen ? 'rotate-45 translate-y-[8px]' : ''
              }`}
            />
            <span
              className={`block w-full h-[2px] bg-[#0B3D91] transition-all duration-300 ${
                menuOpen ? 'opacity-0' : ''
              }`}
            />
            <span
              className={`block w-full h-[2px] bg-[#0B3D91] transition-all duration-300 ${
                menuOpen ? '-rotate-45 -translate-y-[8px]' : ''
              }`}
            />
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden absolute top-[72px] left-0 right-0 bg-white shadow-md flex flex-col p-8 gap-6 z-50">
          <div className="flex flex-col gap-3">
            <span className="text-xs font-bold uppercase tracking-wider text-[#0B3D91]">Solutions</span>
            <a href={`${MAIN_SITE}/solutions/financial-services`} className="text-sm font-medium text-[#333] hover:text-[#0B3D91] pl-3">Financial Services &amp; Accounting</a>
            <a href={`${MAIN_SITE}/solutions/underwriters`} className="text-sm font-medium text-[#333] hover:text-[#0B3D91] pl-3">Insurance Underwriters</a>
            <a href={`${MAIN_SITE}/solutions/cpg`} className="text-sm font-medium text-[#333] hover:text-[#0B3D91] pl-3">CPG &amp; Brand Management</a>
          </div>
          <a href={`${MAIN_SITE}/customers/`} className="text-sm font-medium text-[#333] hover:text-[#0B3D91]">Customers</a>
          <a href={`${MAIN_SITE}/pricing`} className="text-sm font-medium text-[#333] hover:text-[#0B3D91]">Pricing</a>
          <a href={`${MAIN_SITE}/trust`} className="text-sm font-medium text-[#333] hover:text-[#0B3D91]">Trust Center</a>
          <div className="flex flex-col gap-3">
            <span className="text-xs font-bold uppercase tracking-wider text-[#0B3D91]">News</span>
            <a href={`${MAIN_SITE}/blogs/`} className="text-sm font-medium text-[#333] hover:text-[#0B3D91] pl-3">Blogs</a>
            <a href={`${MAIN_SITE}/press/`} className="text-sm font-medium text-[#333] hover:text-[#0B3D91] pl-3">Press</a>
            <a href={`${MAIN_SITE}/releases/`} className="text-sm font-medium text-[#333] hover:text-[#0B3D91] pl-3">Release Notes</a>
          </div>
          <a href={`${MAIN_SITE}/contact`} className="text-sm font-medium text-[#333] hover:text-[#0B3D91]">Contact</a>
          <Link to="/" className={`text-sm font-medium ${isAssessment ? 'text-[#0B3D91]' : 'text-[#333] hover:text-[#0B3D91]'}`}>
            Assessment
          </Link>
          <a href="https://chatapp.nera.systems" className="text-sm font-semibold text-white bg-[#0B3D91] rounded-full px-5 py-2 text-center">Try it now</a>
        </div>
      )}
    </header>
  );
}
